import { API_BASE } from "./config";
import { newId } from "./reading";
import { bounceIfUnauthorized } from "./session";

/**
 * Confusion groups — the rail's 混同 (日本-601).
 *
 * A group is a name and a set of NOTE ids, kept in Postgres rather than as Anki tags: adding a card
 * to one mid-review must not be a collection write, because a sitting holds syncing open so `↑` can
 * take an answer back. `noteIds` and `noteCount` are of notes that still exist — the server
 * resolves the stored ids against the collection.
 */
export interface CardGroup {
  id: string;
  name: string;
  createdAt: number;
  updatedAt: number;
  lastUsedAt: number;
  noteCount: number;
  noteIds: number[];
}

/** Thrown on a 409 — the name is taken. Carries the server's own sentence, which names the group. */
export class GroupNameTaken extends Error {}

async function request<T>(path: string, init?: RequestInit): Promise<T> {
  const res = await fetch(`${API_BASE}${path}`, { credentials: "include", ...init });
  if (!res.ok) {
    bounceIfUnauthorized(res);
    const detail = await res
      .json()
      .then((body) => (body as { detail?: unknown }).detail)
      .catch(() => undefined);
    const message = typeof detail === "string" && detail ? detail : `Request failed (${res.status})`;
    if (res.status === 409) throw new GroupNameTaken(message);
    throw new Error(message);
  }
  if (res.status === 204) return undefined as T;
  return (await res.json()) as T;
}

function send<T>(method: string, path: string, body?: unknown): Promise<T> {
  return request<T>(path, {
    method,
    headers: { "Content-Type": "application/json" },
    body: body === undefined ? undefined : JSON.stringify(body),
  });
}

export async function listGroups(): Promise<CardGroup[]> {
  return (await request<{ groups: CardGroup[] }>("/api/groups")).groups;
}

/** Create a group, optionally with its first notes in the same request. The id is minted here, so
 *  a retry after a lost response is the same group rather than a second one. */
export function createGroup(name: string, noteIds: number[] = []): Promise<CardGroup> {
  return send<CardGroup>("POST", "/api/groups", { id: newId(), name, noteIds });
}

export function renameGroup(id: string, name: string): Promise<CardGroup> {
  return send<CardGroup>("PATCH", `/api/groups/${encodeURIComponent(id)}`, { name });
}

/** Deletes the group and its memberships. **Never a note.** */
export function deleteGroup(id: string): Promise<void> {
  return send<void>("DELETE", `/api/groups/${encodeURIComponent(id)}`);
}

export async function addToGroup(id: string, noteIds: number[]): Promise<number> {
  const body = await send<{ changed: number }>(
    "POST",
    `/api/groups/${encodeURIComponent(id)}/notes/add`,
    { noteIds },
  );
  return body.changed;
}

export async function removeFromGroup(id: string, noteIds: number[]): Promise<number> {
  const body = await send<{ changed: number }>(
    "POST",
    `/api/groups/${encodeURIComponent(id)}/notes/remove`,
    { noteIds },
  );
  return body.changed;
}

/** Note id → the ids of the groups it is in. A note in none is absent. */
export async function groupMembership(noteIds: number[]): Promise<Map<number, string[]>> {
  if (noteIds.length === 0) return new Map();
  const body = await send<{ membership: Record<string, string[]> }>(
    "POST",
    "/api/groups/membership",
    { noteIds },
  );
  return new Map(Object.entries(body.membership).map(([k, v]) => [Number(k), v]));
}

/** `nid:1,2,3` — Anki's own search for a set of notes, which is how a group scopes 単語. */
export function nidClause(noteIds: readonly number[]): string {
  return `nid:${[...new Set(noteIds)].join(",")}`;
}
